import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { AccountQueryKeys } from '~/types/account';
import {
  Consent,
  ImportableConsent,
  ConsentQueryKeys,
  ConsentsMeta,
  ConsentStatus,
  CreateConsentRequestAttributes,
} from '~/types/consent';
import { Institution, InstitutionType } from '~/types/institution';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { assertIsDefined } from '~/types/typeGuards';
import { isConsentVisibleToUser, UpdateConsentData } from '~/utils/consents';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
} from '~/utils/jsonapi-query';
import { useSwanInstitution } from './institutions';

export const invalidateConsentCaches = (): void => {
  queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
};

class CreateConsentTransformer extends Transformer<CreateConsentRequestAttributes, {}> {
  constructor({ hasForConsent }: { hasForConsent: boolean }) {
    super();
    this.type = 'consent';
    this.relationships = {
      company: (payload: CreateConsentRequestAttributes) =>
        transform()
          .withInput({ id: payload.companyId })
          .withTransformer(new DefaultTransformer('company'))
          .withIncluded(false)
          .toContext(),
      institution: (payload: CreateConsentRequestAttributes) =>
        transform()
          .withInput({ id: payload.institutionId })
          .withTransformer(new DefaultTransformer('institution'))
          .withIncluded(false)
          .toContext(),
      ...(hasForConsent
        ? {
            forConsent: (payload: CreateConsentRequestAttributes) =>
              transform()
                .withInput(payload.forConsent ? { id: payload.forConsent } : null)
                .withTransformer(new DefaultTransformer('consent'))
                .withIncluded(false)
                .toContext(),
          }
        : {}),
    };
  }

  transform(payload: CreateConsentRequestAttributes): JsonObject {
    const { companyId: _companyId, institutionId: _institutionId, forConsent: _forConsent, purpose, ...rest } = payload;

    return {
      ...rest,
      purpose: purpose ?? (payload.forConsent ? 'IMPORT' : 'SYNC'),
    };
  }
}

const createConsent = async (fields: CreateConsentRequestAttributes): Promise<Consent> => {
  const payload = transform<CreateConsentRequestAttributes, JsonObject>()
    .withInput(fields)
    .withTransformer(new CreateConsentTransformer({ hasForConsent: !!fields.forConsent }))
    .serialize();

  const { data } = await backendApiGateway.post<DocumentObject>('api/v1/consents', payload, {
    params: { include: 'institution,accounts,company' },
  });
  return deserialize<Consent>(data) as Consent;
};

export interface CreateConsentOptions {
  forConsent?: Consent['id'];
  institutionId: Institution['id'];
  purpose?: Consent['purpose'];
}

export const useCreateConsent = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: ({ forConsent, institutionId, purpose }: CreateConsentOptions) =>
      createConsent({ companyId, forConsent, institutionId, purpose }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

export const useCreateSwanConsent = () => {
  const swanInstitution = useSwanInstitution();
  const companyId = useActiveCompanyId();
  const deleteConsentsByInstitution = useDeleteConsentsWithoutAccounts();

  return useMutation({
    mutationFn: async () => {
      assertIsDefined(swanInstitution, 'Swan institution is not defined');
      await deleteConsentsByInstitution(swanInstitution.id);
      return createConsent({
        companyId,
        institutionId: swanInstitution.id,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

const createManualConsent = async (companyId: string): Promise<Consent> => {
  const request = {
    data: {
      attributes: {
        purpose: 'SYNC',
      },
      relationships: {
        company: {
          data: {
            id: companyId,
            type: 'company',
          },
        },
      },
      type: 'consent',
    },
  };
  const { data } = await backendApiGateway.post<DocumentObject>('api/v1/consents', request, {
    params: { include: 'institution,accounts' },
  });
  return deserialize<Consent>(data) as Consent;
};

export const useCreateManualConsent = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: () => createManualConsent(companyId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

const fetchConsent = async (consentId: Consent['id']): Promise<Consent> => {
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/consents/${consentId}?include=institution`);
  return deserialize<Consent>(data) as Consent;
};

export const useConsentQuery = (consentId: Consent['id'] | null) => {
  return useQuery({
    queryKey: ConsentQueryKeys.single(consentId),
    queryFn: () => fetchConsent(consentId ?? ''),
    enabled: !!consentId,
  });
};

interface ImportableConsentsMeta {
  importedParentConsentIds?: string[];
}

const importedParentConsentIdsFromMeta = (document: unknown): string[] => {
  if (typeof document !== 'object' || document === null) {
    return [];
  }

  const meta = (document as Record<string, unknown>).meta;
  if (typeof meta !== 'object' || meta === null) {
    return [];
  }

  const importedParentConsentIds = (meta as ImportableConsentsMeta).importedParentConsentIds;
  return importedParentConsentIds ?? [];
};

const fetchImportableConsents = async (companyId: string): Promise<ImportableConsent[]> => {
  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/consents/importable', {
    params: {
      company_id: companyId,
      include: 'company,institution,accounts',
    },
  });

  const consents = deserialize<Consent>(data) as Consent[];
  const importedParentConsentIds = new Set(importedParentConsentIdsFromMeta(data));

  return consents.map((consent) => ({
    ...consent,
    alreadyImported: importedParentConsentIds.has(consent.id),
  }));
};

export const useImportableConsents = (enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: ['Consent', 'Importable', companyId],
    queryFn: () => fetchImportableConsents(companyId),
    enabled: enabled && !!companyId,
  });
};

const fetchConsents = async (companyId: string, type?: InstitutionType, manual?: boolean): Promise<Consent[]> => {
  const pageSize = 100;

  let allConsents: Consent[] = [];
  let fetchedConsents: Consent[] = [];
  let cursor: MetaDataCursorPage['cursor'] | null = null;

  do {
    const filtersBuilder = jsonAPIFilterBuilder();
    if (companyId) {
      filtersBuilder.eq('company.id', companyId);
    }
    if (type) {
      filtersBuilder.contains('institution.types', type);
    }
    if (manual) {
      filtersBuilder.empty('institution.id', true);
    }
    filtersBuilder.eq('purpose', 'SYNC');

    const includeBuilder = jsonAPIIncludeBuilder(['institution', 'accounts', 'user']);
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: pageSize });
    if (cursor) {
      pageBuilder.cursor(cursor);
    }

    const queryParams = buildJsonAPIQueryParams(filtersBuilder, includeBuilder, pageBuilder);

    const { data: results } = await backendApiGateway.get<DocumentObject & { meta: ConsentsMeta }>('/api/v1/consents', {
      params: queryParams,
    });

    fetchedConsents = deserialize<Consent>(results) as Consent[];
    allConsents = allConsents.concat(fetchedConsents);
    const meta = pagedMetaSchema.parse(results.meta);

    cursor = meta.page.cursor;
  } while (fetchedConsents.length === pageSize);

  return allConsents;
};

export const useBankingConsents = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: ConsentQueryKeys.company(companyId, InstitutionType.Banking),
    queryFn: async () => {
      const [bankingConsents, manualConsents] = await Promise.all([
        fetchConsents(companyId, InstitutionType.Banking),
        fetchConsents(companyId, undefined, true),
      ]);
      return [...bankingConsents, ...manualConsents];
    },
    enabled: !!companyId,
    staleTime: 300_000,
    refetchOnMount: false,
  });
};

export const useAccountingConsents = (disabled = false) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: ConsentQueryKeys.company(companyId, InstitutionType.Accounting),
    queryFn: () => fetchConsents(companyId, InstitutionType.Accounting),
    enabled: !!companyId && !disabled,
    staleTime: 300_000,
    refetchOnMount: false,
  });
};

const updateConsentUserData = async ({ consentId, userData, debug }: UpdateConsentData): Promise<Consent> => {
  const request = {
    data: {
      attributes: {
        userData,
        debug,
      },
      relationships: {},
      id: consentId,
      type: 'consent',
    },
  };

  const { data } = await backendApiGateway.patch<{}, AxiosResponse<DocumentObject>>(
    `/api/v1/consents/${consentId}`,
    request,
    { params: { include: 'institution,accounts,company' } },
  );
  const consent = deserialize(data) as Consent;
  return consent;
};

export const useUpdateConsentUserData = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: updateConsentUserData,
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.single(variables.consentId) });
    },
  });
};

const setConsentStatus = async (consentId: Consent['id'], status: ConsentStatus): Promise<Consent> => {
  const { data } = await backendApiGateway.patch<{}, AxiosResponse<DocumentObject>>(
    `/api/v1/consents/${consentId}?include=institution`,
    {
      data: {
        attributes: { status },
        relationships: {},
        id: consentId,
        type: 'consent',
      },
    },
  );
  return deserialize(data) as Consent;
};

const reauthoriseConsent = async (consentId: Consent['id']): Promise<Consent> => {
  const { data } = await backendApiGateway.post<{}, AxiosResponse<DocumentObject>>(
    `/api/v1/consents/${consentId}/reauthorization`,
    undefined,
    { params: { include: 'institution' } },
  );
  return deserialize(data) as Consent;
};

const useSetConsentStatus = (status: ConsentStatus) => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: ({ consentId }: { consentId: Consent['id'] }) => setConsentStatus(consentId, status),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.single(variables.consentId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

export const useReAuthoriseConsent = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: ({ consentId }: { consentId: Consent['id'] }) => reauthoriseConsent(consentId),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.single(variables.consentId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};
export const useEnableConsent = () => useSetConsentStatus(ConsentStatus.Pending);
export const useDisableConsent = () => useSetConsentStatus(ConsentStatus.Disabled);

const deleteConsent = async ({ consentId }: { consentId: Consent['id'] }) => {
  return await backendApiGateway.delete(`/api/v1/consents/${consentId}`);
};

export const useDeleteConsent = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: deleteConsent,
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.single(variables.consentId) });
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

const bulkDeleteConsent = async ({ consentIds }: { consentIds: Consent['id'][] }) => {
  for (const consentId of consentIds) {
    await deleteConsent({ consentId });
  }
};

const useBulkDeleteConsent = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: bulkDeleteConsent,
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.companyRoot(companyId) });
      for (const consentId of variables.consentIds) {
        queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.single(consentId) });
      }
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
    },
  });
};

export const useDeleteConsentsWithoutAccounts = () => {
  const existingConsents = useBankingConsents();
  const bulkDeleteConsent = useBulkDeleteConsent();
  const { captureException } = useExceptionLogger();

  const splitConsentsByVisibility = (consents: Consent[]) => {
    const { visible, invisible } = consents.reduce(
      (acc, consent) => {
        if (isConsentVisibleToUser(consent)) {
          acc.visible.push(consent);
        } else {
          acc.invisible.push(consent);
        }
        return acc;
      },
      { visible: [], invisible: [] } as { visible: Consent[]; invisible: Consent[] },
    );
    return { visible, invisible };
  };

  const ensureNoConsentsWithoutAccounts = async (consents: Consent[]) => {
    const { visible: toKeep, invisible: toRemove } = splitConsentsByVisibility(consents);
    if (isEmpty(toRemove)) {
      return toKeep;
    }

    try {
      await bulkDeleteConsent.mutateAsync({ consentIds: toRemove.map((el) => el.id) });
      return toKeep;
    } catch (error) {
      captureException(error, {
        extra: {
          consent_ids: toRemove.map((el) => el.id),
        },
      });
      return consents;
    }
  };

  return async (institutionId: Institution['id']) => {
    const consentsWithSameInstitution = (existingConsents.data || []).filter(
      (consent) => consent.institution?.id === institutionId,
    );
    return await ensureNoConsentsWithoutAccounts(consentsWithSameInstitution);
  };
};
